<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div
    :class="[
      layoutTypeClass,
      navbarClasses,
      footerClasses,
      { 'no-scroll': isAppPage },
    ]"
    class="layout--main"
  >
    <v-nav-menu
      :nav-menu-items="navMenuItems"
      title="UBUN"
      parent=".layout--main"
    />

    <div
      id="content-area"
      :class="[contentAreaClass, { 'show-overlay': bodyOverlay }]"
    >
      <div id="content-overlay" />

      <!-- Navbar -->
      <the-navbar-vertical
        :navbar-color="navbarColor"
        :class="[
          { 'text-white': isNavbarDark && !isThemeDark },
          { 'text-base': !isNavbarDark && isThemeDark },
        ]"
      />
      <!-- /Navbar -->

      <div class="content-wrapper">
        <div class="router-view">
          <div class="router-content">
            <transition :name="routerTransition">
              <div
                v-if="$route.meta.breadcrumb || $route.meta.pageTitle || $route.meta.subtitle"
                class="router-header flex flex-wrap items-center mb-6"
              >
                <div
                  :class="{
                    'pr-4 border-0 md:border-r border-solid border-grey-light':
                      $route.meta.breadcrumb,
                  }"
                  class="content-area__heading"
                >
                  <h2 class="mb-1">
                    {{ routeTitle }}
                  </h2>
                </div>

                <div v-if="$route.meta.subtitle">
                  <p class="ml-4">
                    {{ $route.meta.subtitle }}
                  </p>
                </div>

                <!-- BREADCRUMB -->
                <vx-breadcrumb
                  v-if="$route.meta.breadcrumb"
                  :route="$route"
                  class="ml-4 md:block hidden"
                />

                <div
                  v-if="shouldShowReportCollectedPeriod"
                  class="ml-auto"
                >
                  集計期間 {{ activeAccount.reportCollectedPeriod.since }} 〜 {{ activeAccount.reportCollectedPeriod.until }}
                </div>
                <div
                  v-if="shouldShowSalesReportCollectedPeriod"
                  class="ml-auto"
                >
                  集計期間 {{ activeAccount.salesReportCollectedPeriod.since }} 〜 {{ activeAccount.salesReportCollectedPeriod.until }}
                </div>
                <div
                  v-if="shouldShowSalesReportNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・Ubun BASEの売上レポートは、セラーセントラルの「ビジネスレポート」とamazon adsの「スポンサー広告レポート」をAPIで取得し、ASINをキーに統合しています。
                    ・「ビジネスレポート」は税込み、「スポンサー広告レポート」は税抜き価格です。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowBusinessReportNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・Ubun BASEの利益レポートは、セラーセントラルの「決済レポートV2」とamazon adsの「スポンサー広告レポート」をAPIで取得し、ASINをキーに統合しています。
                    ・「決済レポートV2」、「スポンサー広告レポート」共に税込み価格です。
                    ・「決済レポートV2」は注文ごとにレポートに反映されるまで時差が異なります。詳しくはセラーセントラルの決済レポートヘルプページを参照してください。
                    ・Ubun BASEの利益レポートに含まれる項目は<a
                      :href="reportUrl"
                      target="_blank"
                      rel="nofollow"
                    >こちら</a>から確認してください。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowMonitorKeywordNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・キーワードモニタリングを設定すると該当のキーワードにおいて、Amazonの検索結果画面の何番目に自社のASINが掲載されるかを、日次でモニタリングすることができます。（PCで2ページ目まで）
                    ・モニタリング結果は、「Analytics」や「SlideReport」機能で確認することができます。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowMonitorAsinNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・ASINモニタリングを設定すると、該当のASINの「ベストセラーランキング」を日次でモニタリングすることができます。
                    ・モニタリング結果は、「Analytics」機能で確認することができます。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowMeasureNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・売上を拡大するためにおこなった各種施策を登録することができます。登録された施策の検証期間が終了すると自動的にレポートを作成しメールします。
                    ・比較期間中の売上かKPIに設定した指標のどちらかが0だった場合はステータスが「データなし」になります。この場合は期間を変えて再度施策登録をおこなってください。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowAnalysisNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・ユーザーが投稿したレビューに基づいて分析を行います。レビュー自体に商品に関する誤った情報がある場合は分析結果に直接影響します。
                    ・AIによる分析結果はその正確性を保証するものではございません。元のレビューと照らし合わせて適切な情報かどうかを確認してください。
                    ・分析精度を向上させるために継続的に改善をおこないます。不適切な分析結果があった場合は<a
                      href="https://docs.google.com/forms/d/e/1FAIpQLSdhQkHiZkq3nsHAwq3xZvHB5YfPlzG2ALQfpjAAYLPGgtyKbA/viewform?usp=sf_link"
                      target="_blank"
                    >こちら</a>からフィードバックをお願いいたします。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowAdBidNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・Ubun BASEの自動入札機能はルールに基づいて稼働します。最適な結果を得るためには、適切なルール設定が必要です。
                    ・自動入札機能の詳細については<a
                      :href="adBidUrl"
                      target="_blank"
                      rel="nofollow"
                    >[こちら]</a>をご確認の上、ルールの登録を行ってください。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowReviewRequestNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・商品レビューや出品者の評価を（注文ごとに）繰り返しリクエストすることは、Amazonのポリシーに反しています。
                    ・「レビューリクエスト」機能を使用することにより、 マーケットプレイス・メッセージ管理から追加でレビューリクエストを送信しないことに同意したものとみなされます。
                    ・この機能を使用すると条件に一致する注文に対し、商品と出品者のレビューをリクエストするEメールがカスタマーに送信されます。
                    ・レビューリクエストは、自動的にカスタマーの希望する言語に翻訳されます。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowOauthNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・下記リンクから、Amazon seller centralとadvertisingのアカウントの接続認証をおこなってください。（Amazonサイトにリンクします。）
                    ・連携が完了すると過去のデータ取得をおこないます。最大で48時間程度時間がかかる場合がございます。データ反映までしばらくお待ちください。
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowReoauthNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・AmazonのAPIのバージョンUPに伴い、Ubun BASEを引き続きご利用いただくためには、Amazon seller centralアカウントとの再接続が必要となりました。
                    ・下記リンクから、Amazon seller centralのアカウントとの接続認証をおこなってください。（Amazonサイトにリンクします。） 
                    ・連携が完了すると過去データの収集をおこないます。データ反映に最大48時間程度かかりますのでしばらくお待ちください。 
                  </paragraph>
                </div>
                <div
                  v-if="shouldShowAMCNote"
                  class="w-full"
                >
                  <paragraph class="pt-5 text-sm">・AMC（Amazo Marketing Cloud）の活用方法を詳しく知りたい方は、<a
                      href="https://ubunbase.ubun.jp/blog/"
                      target="_blank"
                      rel="nofollow"
                    >[こちら]</a>をご覧ください。
                  </paragraph>
                </div>
              </div>
            </transition>

            <div class="content-area__content">
              <back-to-top
                v-if="!hideScrollToTop"
                right="30px"
                bottom="5%"
                visibleoffset="500"
              >
                <vs-button
                  icon-pack="feather"
                  icon="icon-arrow-up"
                  class="shadow-lg btn-back-to-top"
                />
              </back-to-top>

              <router-view
                v-slot="{ Component }"
                @change-route-title="changeRouteTitle"
                @set-app-classes="(classesStr) => $emit('setAppClasses', classesStr)"
              >
                <transition
                  :name="routerTransition"
                  mode="out-in"
                >
                  <component :is="Component" />
                </transition>
              </router-view>
            </div>
          </div>
        </div>
      </div>
      <the-footer />
    </div>
    <template v-if="newsTitle && newsText && newsReleaseDate">
      <confirm-modal
        v-model="showNews"
        :title="newsTitle"
        hide-cancel
        confirm-button-text="閉じる"
        @click:confirm="showNews = false"
      >
        <template #content>
          <div class="block text-sm">
            {{ newsReleaseDate }}
          </div>
          <div
            class="mt-2 whitespace-pre-line break-words"
            v-html="newsText"
          />
        </template>
      </confirm-modal>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import BackToTop from '@/components/BackToTop.vue'
import defaultNavMenuItems from '@/user/components/layouts/components/vertical-nav-menu/navMenuItems.js'
import TheNavbarVertical from '@/user/components/layouts/components/navbar/TheNavbarVertical.vue'
import TheFooter from '@/user/components/layouts/components/TheFooter.vue'
import themeConfig from '../../../../../themeConfig.js'
import VNavMenu from '@/user/components/layouts/components/vertical-nav-menu/VerticalNavMenu.vue'
import { MAINTENACE_ERROR, UNAUTHORIZED_ERROR } from '@/user/store/errorTypes'
import ConfirmModal from '@/components/modals/ConfirmModal'
import Paragraph from '@/components/Paragraph'
import { replaceUrlStringWithATag } from '@/utils'
import moment from 'moment'

export default {
  name: 'LayoutMain',
  components: {
    ConfirmModal,
    BackToTop,
    Paragraph,
    TheFooter,
    TheNavbarVertical,
    VNavMenu
  },
  emits: ['setAppClasses'],
  data () {
    return {
      footerType: themeConfig.footerType || 'static',
      hideScrollToTop: themeConfig.hideScrollToTop,
      isNavbarDark: false,
      navbarColor: themeConfig.navbarColor || '#fff',
      navbarType: themeConfig.navbarType || 'floating',
      routerTransition: themeConfig.routerTransition || 'none',
      routeTitle: this.$route.meta.pageTitle,
      loader: null,
      shownError: {},
      showNews: true,
      newsTitle: '',
      newsText: '',
      newsReleaseDate: '',
      reportUrl: `${process.env.VUE_APP_BASE_API_URL}/api/resource?u=https%3A%2F%2Fresource.ubun.jp%2F2022%2F05%2F19%2Fprofit_report%2F`,
      adBidUrl: `${process.env.VUE_APP_BASE_API_URL}/api/resource?u=https%3A%2F%2Fresource.ubun.jp%2F2023%2F11%2F20%2Fautobid%2F`
    }
  },
  computed: {
    ...mapState(['globalError', 'requestingStatus']),
    ...mapGetters('account', ['isActiveAccountConnected', 'isActiveAccountAdConnected', 'isActiveAccountNeedReauth']),
    ...mapState('account', ['activeAccount']),
    ...mapState('amcProfile', ['activeAmcProfile']),
    ...mapState('news', ['newsList']),
    navMenuItems () {
      let navMenuItems = []
      // SP-API: 未接続
      if (!this.isActiveAccountConnected) {
        return defaultNavMenuItems.filter(item => item.slug === 'oauth')
      }
      // SP-API: 再認可が必要
      if (this.activeAccount.isNeedReauth) {
        return defaultNavMenuItems.filter(item => item.slug === 'reoauth')
      }
      // SP-API: 接続済
      navMenuItems = structuredClone(defaultNavMenuItems).filter(item => item.slug !== 'reoauth')

      // AMCレポートの表示条件を満たしていない時
      if (!this.activeAccount.amcInitialized) {
        navMenuItems = navMenuItems.filter(item => !['amc-separator', 'amc_report_group', 'amc-reports-audience'].includes(item.slug))
      } else if (!this.activeAmcProfile.hasOwnProperty('isPaidTable') || this.activeAmcProfile.isPaidTable === 0) {
        // 有料契約していない時
        navMenuItems = navMenuItems.map(item => {
          if(item.hasOwnProperty('submenu')) {
            item.submenu = item.submenu.filter(sub => sub.slug !== 'segment')
          }
          return item
        })
      }
      return navMenuItems
    },
    bodyOverlay () {
      return this.$store.state.bodyOverlay
    },
    contentAreaClass () {
      if (this.mainLayoutType === 'vertical') {
        if (this.verticalNavMenuWidth === 'default') return 'content-area-reduced'
        else if (this.verticalNavMenuWidth === 'reduced') return 'content-area-lg'
        else return 'content-area-full'
      } else return 'content-area-full'
    },
    footerClasses () {
      return {
        'footer-hidden': this.footerType === 'hidden',
        'footer-sticky': this.footerType === 'sticky',
        'footer-static': this.footerType === 'static'
      }
    },
    isAppPage () {
      return this.$route.meta.no_scroll
    },
    isThemeDark () {
      return this.$store.state.theme === 'dark'
    },
    layoutTypeClass () {
      return `main-${this.mainLayoutType}`
    },
    mainLayoutType () {
      return this.$store.state.mainLayoutType
    },
    navbarClasses () {
      return {
        'navbar-hidden': this.navbarType === 'hidden',
        'navbar-sticky': this.navbarType === 'sticky',
        'navbar-static': this.navbarType === 'static',
        'navbar-floating': this.navbarType === 'floating'
      }
    },
    verticalNavMenuWidth () {
      return this.$store.state.verticalNavMenuWidth
    },
    windowWidth () {
      return this.$store.state.windowWidth
    },
    latestNews () {
      const latestNews = this.newsList.find(news => {
        const isValid = moment(news.popupDeadline, 'YYYY-MM-DD').isSameOrAfter(moment().startOf('day'))
        return news.readAt === null && isValid
      })
      return latestNews || null
    },
    shouldShowReportCollectedPeriod () {
      // 利益レポートの期間
      return this.$route.path.includes('/reports/') &&
        this.activeAccount.reportCollectedPeriod.since &&
        this.activeAccount.reportCollectedPeriod.until
    },
    shouldShowSalesReportCollectedPeriod () {
      // 売上レポートの期間
      return this.$route.path.includes('/sales-reports/') &&
        this.activeAccount.salesReportCollectedPeriod.since &&
        this.activeAccount.salesReportCollectedPeriod.until
    },
    shouldShowBusinessReportNote () {
      return this.$route.path.includes('/reports/')
    },
    shouldShowSalesReportNote () {
      return this.$route.path.includes('/sales-reports/')
    },
    shouldShowMonitorKeywordNote () {
      return this.$route.path.includes('/monitoring/keyword')
    },
    shouldShowMonitorAsinNote () {
      return this.$route.path.includes('/monitoring/asin')
    },
    shouldShowMeasureNote () {
      return this.$route.path.includes('/measure')
    },
    shouldShowAnalysisNote () {
      return this.$route.path.includes('/review-analysis')
    },
    shouldShowAdBidNote () {
      return this.$route.path.includes('/ad-bid')
    },
    shouldShowReviewRequestNote () {
      return this.$route.path.includes('/review-request')
    },
    shouldShowOauthNote () {
      return this.$route.path.includes('/oauth')
    },
    shouldShowReoauthNote () {
      return this.$route.path.includes('/reoauth')
    },
    shouldShowAMCNote () {
      return this.$route.path.includes('/amc-reports/repeat') || this.$route.path.includes('/amc-reports/audience')
    }
  },
  watch: {
    $route () {
      this.routeTitle = this.$route.meta.pageTitle
    },
    isActiveAccountConnected (val) {
      if (!val && this.$route.path !== '/oauth') {
        this.$router.push('/oauth')
      }
    },
    isActiveAccountNeedReauth (val) {
      if (this.isActiveAccountConnected) {
        if (val && this.$route.path !== '/reoauth') {
          // 再認可が必要なアカウントが再認可画面にいない場合は再認可画面へリダイレクト
          this.$router.push('/reoauth')
        } else if (!val && this.$route.path === '/reoauth') {
          // 再認可が不要なアカウントが再認可画面にいる場合はホームにリダイレクト
          this.$router.push('/')
        }
      }
    },
    isThemeDark (val) {
      const color = this.navbarColor === '#fff' && val ? '#10163a' : '#fff'
      this.updateNavbarColor(color)
    },
    '$store.state.mainLayoutType' (val) {
      this.setNavMenuVisibility(val)
    },
    globalError: {
      deep: true,
      handler (error) {
        const { type, message } = error
        if (type !== this.shownError.type && message !== this.shownError.message) {
          let config = {
            position: 'top-center',
            color: 'danger',
            text: message
          }
          if (type === MAINTENACE_ERROR) {
            if (this.loader) {
              this.loader.hide()
              this.loader = null
            }
            this.shownError = {}
            this.$router.push('/maintenance').catch()
          }
          else if (type === UNAUTHORIZED_ERROR) {
            config = {
              ...config,
              click: async () => {
                await this.logout()
                this.shownError = {}
                await this.$router.push('/login')
              }
            }
            window.setTimeout(async () => {
              await this.logout()
              this.shownError = {}
              await this.$router.push('/login')
            }, 3000)
          } else {
            config = {
              ...config,
              fixed: true,
              click: () => {
                this.shownError = {}
              }
            }
          }
          this.$vs.notify(config)
          this.shownError = error
        }
      }
    },
    requestingStatus (val) {
      if (Object.values(val).includes(true)) {
        if (!this.loader) {
          this.loader = this.$loading.show({
            loader: 'dots'
          })
        }
      } else if (this.loader) {
        this.loader.hide()
        this.loader = null
      }
    },
    latestNews (val) {
      if (val) {
        if (this.$route.path !== '/news') {
          window.setTimeout(() => {
            // 複数回以上出すときに、モーダルが閉じる途中にlatestNewsのtitle, textが変わってしまうため
            // モーダルからは、latestNewsのtitle, textを直接参照させずに newsTitle, newsText を使用する
            this.newsTitle = this.latestNews.title
            this.newsText = replaceUrlStringWithATag(this.latestNews.text)
            this.newsReleaseDate = this.latestNews.releaseDate
            this.showNews = true
          }, 500)
        }
      } else {
        this.showNews = false
      }
    },
    showNews (val) {
      if (!val) {
        this.setNewsRead(this.latestNews.id)
      }
    }
  },
  created () {
    const color = (this.navbarColor === '#fff' && this.isThemeDark) ? '#10163a' : this.navbarColor
    this.updateNavbarColor(color)
    this.setNavMenuVisibility(this.$store.state.mainLayoutType)
  },
  async mounted () {
    await this.listNews({
      page: 1,
      per_page: 10
    })
  },
  methods: {
    ...mapActions('auth', ['logout']),
    ...mapActions('news', ['listNews', 'setNewsRead']),
    changeRouteTitle (title) {
      this.routeTitle = title
    },
    updateNavbarColor (val) {
      this.navbarColor = val
      if (val === '#fff') this.isNavbarDark = false
      else this.isNavbarDark = true
    },
    setNavMenuVisibility (layoutType) {
      if (
        (layoutType === 'horizontal' && this.windowWidth >= 1200) ||
        (layoutType === 'vertical' && this.windowWidth < 1200)
      ) {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false)
        this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu')
      } else {
        this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)
      }
    }
  }
}
</script>
